/** @file ringFacing: where a gizmo ring faces the viewer (the centre of its near half) and how squarely. */
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { GizmoModel } from './gizmoModel';

/** How far gizmo axis `i` points toward the viewer in display space. */
function toward(model: GizmoModel, view: View, i: number): number {
  const a = model.axes[i] ?? [0, 0, 0];
  const b = view.basis.back;
  return a[0] * b[0] + a[1] * b[1] + a[2] * view.zScaleView * b[2];
}

/**
 * For ring `k` (`cos t e_u + sin t e_v`, u = k + 1, v = k + 2): `out[0]` = the ring angle `t` nearest the viewer
 * (the near half is `t +- pi / 2` around it) and `out[1]` = |cos| of the angle between the ring's axis and the view
 * direction (1 face-on, 0 edge-on), both in display space (z scaled by `zScaleView`). Writes into `out`.
 */
export function ringFacing(model: GizmoModel, view: View, k: 0 | 1 | 2, out: [number, number]): [number, number] {
  const n = toward(model, view, k);
  const u = toward(model, view, (k + 1) % 3);
  const v = toward(model, view, (k + 2) % 3);
  out[0] = Math.atan2(v, u);
  out[1] = Math.abs(n) / (Math.hypot(n, u, v) || 1);
  return out;
}
